import { useEffect, useState } from "react"
import DotsLoader from "components/DotsLoader"
import TokenImage from "components/TokenImage"
import { getContractChainId } from "utils/contractInfoHelper"
import { getLiveGeneratorUrl } from "utils/liveGenerator"

interface Props {
  contractAddress: string
  tokenId: string
  width: number
  height: number
  chainId?: number
  generatorUrl?: string
  tokenHash?: string
  imageUrl?: string
  inert?: boolean
}

const TokenLive = ({
  contractAddress,
  tokenId,
  width,
  height,
  chainId,
  generatorUrl,
  tokenHash,
  imageUrl,
  inert = false,
}: Props) => {
  const [loaded, setLoaded] = useState(false)
  const resolvedChainId = getContractChainId(contractAddress, chainId)
  const endpoint = getLiveGeneratorUrl({
    contractAddress,
    tokenId,
    chainId: resolvedChainId,
    metadataGeneratorUrl: generatorUrl,
    tokenHash,
  })

  useEffect(() => {
    setLoaded(false)
    if (!endpoint) return

    // Safety net: some generator pages keep the connection open or never fire a
    // load event for the iframe, which would otherwise leave the loader spinning
    // forever. Reveal the (progressively rendering) iframe after a short delay.
    const timer = window.setTimeout(() => setLoaded(true), 4000)
    return () => window.clearTimeout(timer)
  }, [endpoint])

  if (!endpoint) {
    return <TokenImage contractAddress={contractAddress} tokenId={tokenId} width={width} height={height} chainId={chainId} imageUrl={imageUrl} />
  }

  return (
    <div
      style={{ width, height }}
      className="relative bg-gallery-surface overflow-hidden"
      aria-busy={!loaded}
    >
      {!loaded && (
        <div className="gallery-image-loader z-10">
          <DotsLoader label="Loading live view" shuffleKey={endpoint} />
        </div>
      )}
      <iframe
        title={`Live token ${tokenId}`}
        src={endpoint}
        width={width}
        height={height}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        loading="eager"
        className={`w-full h-full transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        style={{ display: "block", border: "none", pointerEvents: inert ? "none" : undefined }}
      />
    </div>
  )
}

export default TokenLive
